import { createContext, useContext, useEffect, useState } from "react";
import { getAllData, postAllData, getProjectById } from "../api";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { useGeneralContext } from "../../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";
import {
  computeShellUID,
  computeNozzleUID,
} from "../../../../../utils/pvUID";

const PVContext = createContext();

const DESIGN_FIELD_BINDINGS = {
  vessel: (payload) => payload?.dataVessel?.PnPID,
  designExternalPressure: (payload) => payload?.dataVessel?.Designextpressure,
  diameterBasis: (payload) => payload?.dataVessel?.DiameterBasis,
  headType: (payload) => payload?.dataVessel?.HeadType,
  supportType: (payload) => payload?.dataVessel?.SupportType,
  liquidHeight: (payload) => payload?.dataVessel?.LiquidHeight,
  liquidDensity: (payload) => payload?.dataVessel?.LiquidDensity,
  insulationDensity: (payload) => payload?.dataVessel?.InsulationDensity,
  impactTest: (payload) => payload?.dataVessel?.ImpactTest,
  hydrotestDesignBasis: (payload) => payload?.dataVessel?.HydrotestDesign,
  hydrotestPressure: (payload) => payload?.dataVessel?.HydrotestPressure,
  jointEffCirc: (payload) => payload?.dataVessel?.JointEfficiencyCirc,
  jointEffLong: (payload) => payload?.dataVessel?.JointEfficiencyLong,
  mdmt: (payload) => payload?.dataVessel?.MDMT,
  orientation: (payload) => payload?.dataVessel?.Orientation,
  seismicZone: (payload) => payload?.dataVessel?.SeismicDesign,
  windSpeed: (payload) => payload?.dataVessel?.WindSpeed,
  windDesignCode: (payload) => payload?.dataVessel?.WindDesignCode,
  radiography: (payload) => payload?.dataVessel?.Radiography,
  pwht: (payload) => payload?.dataVessel?.PWHT,
  tagNo: (payload) => payload?.dataEquip?.Tag,
  vesselDiameter: (payload) => payload?.dataTank?.Diameter,
  vesselLengthTT: (payload) => payload?.dataTank?.Height,
  vesselLengthSS: (payload) => payload?.dataTank?.Width,
  insulationThk: (payload) => payload?.dataTank?.Insulation,
  designInternalPressure: (payload) => payload?.dataTank?.DesignPressure,
  designTemperature: (payload) => payload?.dataTank?.DesignTemperature,
  corrosionAllowanceShell: (payload) => payload?.dataVessel?.CorrosionAllowance,
  corrosionAllowanceNozzle: (payload) => payload?.dataVessel?.CorrosionAllowanceNozzle,
  flangeRating: (payload) => payload?.dataVessel?.FlangeRating,
  asmeB165Group: (payload) => payload?.dataVessel?.ASMEB165Group,
  projectNo: (payload) => payload?.dataVessel?.ProjectNo,
  revNo: (payload) => payload?.dataVessel?.RevNo,
  client: (payload) => payload?.dataVessel?.Client,
  crn: (payload) => payload?.dataVessel?.CRN,
  designCode: (payload) => payload?.dataVessel?.DesignCode,
  editionAddenda: (payload) => payload?.dataVessel?.EditionAddenda,
  uStamp: (payload) => payload?.dataVessel?.UStamp,
  siUnits: (payload) => payload?.dataVessel?.SIUnits,
  engUnits: (payload) => payload?.dataVessel?.EnglishUnits,
  shellMaterial: (payload) => payload?.dataVessel?.ShellMaterial,
  headMaterial: (payload) => payload?.dataVessel?.HeadMaterial,
  nozzleMaterial: (payload) => payload?.dataVessel?.NozzleMaterial,
  flangeMaterial: (payload) => payload?.dataVessel?.FlangeForging,
  rfPadMaterial: (payload) => payload?.dataVessel?.RFPadMaterial,
  studBoltMaterial: (payload) => payload?.dataVessel?.StudBoltNutMaterial,
  gasketMaterial: (payload) => payload?.dataVessel?.GasketMaterial,
  shellMaterialReq: (payload) => payload?.dataVessel?.ShellMaterialReq,
  headMaterialReq: (payload) => payload?.dataVessel?.HeadMaterialReq,
  nozzleMaterialReq: (payload) => payload?.dataVessel?.NozzleMaterialReq,
  flangeMaterialReq: (payload) => payload?.dataVessel?.FlangeForgingReq,
  rfPadMaterialReq: (payload) => payload?.dataVessel?.RFPadMaterialReq,
  studBoltMaterialReq: (payload) => payload?.dataVessel?.StudBoltNutMaterialReq,
  gasketMaterialReq: (payload) => payload?.dataVessel?.GasketMaterialReq,
  shellStress: (payload) => payload?.dataVessel?.ShellStress,
  shellCurve: (payload) => payload?.dataVessel?.ShellCurve,
  headStress: (payload) => payload?.dataVessel?.HeadStress,
  headCurve: (payload) => payload?.dataVessel?.HeadCurve,
  nozzleStress: (payload) => payload?.dataVessel?.NozzleStress,
  nozzleCurve: (payload) => payload?.dataVessel?.NozzleCurve,
  flangeStress: (payload) => payload?.dataVessel?.FlangeStress,
  flangeCurve: (payload) => payload?.dataVessel?.FlangeCurve,
  rfPadStress: (payload) => payload?.dataVessel?.RFPadStress,
  rfPadCurve: (payload) => payload?.dataVessel?.RFPadCurve,
  studBoltStress: (payload) => payload?.dataVessel?.StudBoltNutStress,
  studBoltCurve: (payload) => payload?.dataVessel?.StudBoltNutCurve,
  gasketStress: (payload) => payload?.dataVessel?.GasketStress,
  gasketCurve: (payload) => payload?.dataVessel?.GasketCurve,
};

// Fields belonging to the (currently hidden) Estimation tab. Kept as part of
// the same revision payload so estimation data already on a revision isn't
// lost when the Estimation tab isn't shown in the UI.
const ESTIMATION_FIELD_KEYS = [
  "DataGridView2",
  "DataGridView7",
  "DataGridView8",
  "bomData",
  "totalMaterialCost",
  "totalHours",
  "totalFabCost",
  "totalCost",
  "TextBox41",
  "TextBox35",
];

// Everything left in the form snapshot that isn't design/shell/nozzle/
// estimation is treated as Calculation tab data (txt_*/Label* legacy fields).
const NON_CALCULATION_FIELD_KEYS = new Set([
  ...Object.keys(DESIGN_FIELD_BINDINGS),
  "DataGridView5",
  "DataGridView1",
  "DataGridView10",
  "drawingScale",
  ...ESTIMATION_FIELD_KEYS,
]);

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const normalizeRevisionKey = (value) => {
  const nextValue = String(value ?? "").trim();
  return nextValue || "0";
};

const cloneData = (value) => {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value ?? {}));
  }
};

const normalizeShellRows = (shellSource) => {
  if (Array.isArray(shellSource?.shellDesign)) return shellSource.shellDesign;
  if (Array.isArray(shellSource)) return shellSource;
  return [];
};

const normalizeNozzleRows = (nozzleSource) => {
  const source = isPlainObject(nozzleSource) ? nozzleSource : {};

  return {
    DataGridView1: Array.isArray(source.DataGridView1)
      ? source.DataGridView1
      : Array.isArray(source.nozzlesDesign)
        ? source.nozzlesDesign
        : [],
    DataGridView10: Array.isArray(source.DataGridView10)
      ? source.DataGridView10
      : Array.isArray(source.nozzlesDesign)
        ? source.nozzlesDesign
        : [],
  };
};

const restoreDesignFields = (setValue, payload) => {
  Object.entries(DESIGN_FIELD_BINDINGS).forEach(([fieldName, getter]) => {
    setValue(fieldName, getter(payload));
  });
};

const assignDesignFields = (target, payload) => {
  Object.entries(DESIGN_FIELD_BINDINGS).forEach(([fieldName, getter]) => {
    target[fieldName] = getter(payload);
  });
};

export const usePVContext = () => {
  const context = useContext(PVContext);

  if (!context) {
    throw new Error("usePVContext must be used within a PVProvider");
  }

  return context;
};

function PVProvider({ children }) {
  const [revisionLoadVersion, setRevisionLoadVersion] = useState(0);

  const { id: projectId } = useParams();
  const [selectedProjectId, setSelectedProjectId] = useState(projectId ?? null);
  const activeProjectId = selectedProjectId ?? projectId;

  // Data loads once a revision has been confirmed for the currently
  // selected project (either picked manually, or auto-picked when the
  // project only has one revision) - not immediately on WBS selection.
  const [confirmedRevision, setConfirmedRevision] = useState(null);

  const { control, setValue, getValues, reset } = useFormContext();
  const { selectedModelingRevision, revision } = useGeneralContext();

  const currentRevision = normalizeRevisionKey(
    selectedModelingRevision ?? revision?.name ?? revision,
  );

  // documentUID needs the plant/project actually being worked on - i.e.
  // whichever project is behind activeProjectId (the WBS UID picked via
  // LoadProjectDataComboBox when there is one, otherwise the route's own
  // project) - not some unrelated project tied to the page route.
  const [plantName, setPlantName] = useState("");
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    if (!activeProjectId) {
      setPlantName("");
      setProjectName("");
      return;
    }

    let cancelled = false;

    getProjectById(activeProjectId)
      .then((base) => {
        if (cancelled) return;
        setPlantName(String(base?.plant?.name || "").trim());
        setProjectName(String(base?.name || "").trim());
      })
      .catch((error) => {
        console.error("Failed to fetch plant/project name for documentUID:", error);
        if (!cancelled) {
          setPlantName("");
          setProjectName("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeProjectId]);

  const extractDesignPayload = (formData) => {
    const dataVessel = {};
    const dataTank = {};
    const dataEquip = {};

    dataVessel.PnPID = formData?.vessel;
    dataVessel.Designextpressure = formData?.designExternalPressure;
    dataVessel.DiameterBasis = formData?.diameterBasis;
    dataVessel.HeadType = formData?.headType;
    dataVessel.SupportType = formData?.supportType;
    dataVessel.LiquidHeight = formData?.liquidHeight;
    dataVessel.LiquidDensity = formData?.liquidDensity;
    dataVessel.InsulationDensity = formData?.insulationDensity;
    dataVessel.ImpactTest = formData?.impactTest;
    dataVessel.HydrotestDesign = formData?.hydrotestDesignBasis;
    dataVessel.HydrotestPressure = formData?.hydrotestPressure;
    dataVessel.JointEfficiencyCirc = formData?.jointEffCirc;
    dataVessel.JointEfficiencyLong = formData?.jointEffLong;
    dataVessel.MDMT = formData?.mdmt;
    dataVessel.Orientation = formData?.orientation;
    dataVessel.SeismicDesign = formData?.seismicZone;
    dataVessel.WindSpeed = formData?.windSpeed;
    dataVessel.WindDesignCode = formData?.windDesignCode;
    dataVessel.Radiography = formData?.radiography;
    dataVessel.PWHT = formData?.pwht;
    dataVessel.CorrosionAllowance = formData?.corrosionAllowanceShell;
    dataVessel.CorrosionAllowanceNozzle = formData?.corrosionAllowanceNozzle;
    dataVessel.FlangeRating = formData?.flangeRating;
    dataVessel.ASMEB165Group = formData?.asmeB165Group;
    dataVessel.ProjectNo = formData?.projectNo;
    dataVessel.RevNo = formData?.revNo;
    dataVessel.Client = formData?.client;
    dataVessel.CRN = formData?.crn;
    dataVessel.DesignCode = formData?.designCode;
    dataVessel.EditionAddenda = formData?.editionAddenda;
    dataVessel.UStamp = formData?.uStamp;
    dataVessel.SIUnits = formData?.siUnits;
    dataVessel.EnglishUnits = formData?.engUnits;
    dataVessel.ShellMaterial = formData?.shellMaterial;
    dataVessel.HeadMaterial = formData?.headMaterial;
    dataVessel.NozzleMaterial = formData?.nozzleMaterial;
    dataVessel.FlangeForging = formData?.flangeMaterial;
    dataVessel.RFPadMaterial = formData?.rfPadMaterial;
    dataVessel.StudBoltNutMaterial = formData?.studBoltMaterial;
    dataVessel.GasketMaterial = formData?.gasketMaterial;
    dataVessel.ShellMaterialReq = formData?.shellMaterialReq;
    dataVessel.HeadMaterialReq = formData?.headMaterialReq;
    dataVessel.NozzleMaterialReq = formData?.nozzleMaterialReq;
    dataVessel.FlangeForgingReq = formData?.flangeMaterialReq;
    dataVessel.RFPadMaterialReq = formData?.rfPadMaterialReq;
    dataVessel.StudBoltNutMaterialReq = formData?.studBoltMaterialReq;
    dataVessel.GasketMaterialReq = formData?.gasketMaterialReq;
    dataVessel.ShellStress = formData?.shellStress;
    dataVessel.ShellCurve = formData?.shellCurve;
    dataVessel.HeadStress = formData?.headStress;
    dataVessel.HeadCurve = formData?.headCurve;
    dataVessel.NozzleStress = formData?.nozzleStress;
    dataVessel.NozzleCurve = formData?.nozzleCurve;
    dataVessel.FlangeStress = formData?.flangeStress;
    dataVessel.FlangeCurve = formData?.flangeCurve;
    dataVessel.RFPadStress = formData?.rfPadStress;
    dataVessel.RFPadCurve = formData?.rfPadCurve;
    dataVessel.StudBoltNutStress = formData?.studBoltStress;
    dataVessel.StudBoltNutCurve = formData?.studBoltCurve;
    dataVessel.GasketStress = formData?.gasketStress;
    dataVessel.GasketCurve = formData?.gasketCurve;

    dataEquip.Tag = formData?.tagNo;

    dataTank.Diameter = formData?.vesselDiameter;
    dataTank.Height = formData?.vesselLengthTT;
    dataTank.Width = formData?.vesselLengthSS;
    dataTank.Insulation = formData?.insulationThk;
    dataTank.DesignPressure = formData?.designInternalPressure;
    dataTank.DesignTemperature = formData?.designTemperature;

    return { dataVessel, dataTank, dataEquip };
  };

  const extractNozzleRowsWithGeometry = (formData) => {
    const nozzleRows = Array.isArray(formData?.DataGridView1)
      ? formData.DataGridView1
      : [];

    const geometryRows = Array.isArray(formData?.DataGridView10)
      ? formData.DataGridView10
      : [];

    return nozzleRows.map((row, i) => ({
      ...row,
      ...(geometryRows[i] || {}),
      rowNo: i,
    }));
  };

  async function savePVData() {
    if (!activeProjectId) {
      throw new Error("Cannot save PV data without selectedProjectId/projectId");
    }

    // Let pending onBlur handlers flush their edits before snapshotting.
    await new Promise((resolve) => setTimeout(resolve, 0));

    const formSnapshot = cloneData(getValues());
    const { dataVessel, dataTank, dataEquip } = extractDesignPayload(formSnapshot);

    const shellRows = Array.isArray(formSnapshot?.DataGridView5)
      ? formSnapshot.DataGridView5
      : [];

    const nozzleRowsRaw = Array.isArray(formSnapshot?.DataGridView1)
      ? formSnapshot.DataGridView1
      : [];

    const geometryRowsRaw = Array.isArray(formSnapshot?.DataGridView10)
      ? formSnapshot.DataGridView10
      : [];

    const nozzleRows = extractNozzleRowsWithGeometry(formSnapshot);

    const plantProjectTag = `${plantName || "PLANT"}-${projectName || "PROJECT"}`;
    const documentUID = `${plantProjectTag}-INP${currentRevision}`;
    const estimationDocumentUID = `${plantProjectTag}-EST${currentRevision}`;
    const calculationDocumentUID = `${plantProjectTag}-CALC${currentRevision}`;

    // Estimation tab data - still saved as part of this revision even
    // though the Estimation tab is currently hidden from the UI.
    const estimationRevision = {
      documentUID: estimationDocumentUID,
      DataGridView2: Array.isArray(formSnapshot?.DataGridView2)
        ? formSnapshot.DataGridView2
        : [],
      DataGridView7: Array.isArray(formSnapshot?.DataGridView7)
        ? formSnapshot.DataGridView7
        : [],
      DataGridView8: Array.isArray(formSnapshot?.DataGridView8)
        ? formSnapshot.DataGridView8
        : [],
      bomData: Array.isArray(formSnapshot?.bomData) ? formSnapshot.bomData : [],
      totalMaterialCost: formSnapshot?.totalMaterialCost,
      totalHours: formSnapshot?.totalHours,
      totalFabCost: formSnapshot?.totalFabCost,
      totalCost: formSnapshot?.totalCost,
      TextBox41: formSnapshot?.TextBox41,
      TextBox35: formSnapshot?.TextBox35,
    };

    // Calculation tab data (everything not otherwise accounted for) - same
    // reasoning: kept alive as part of the revision while its tab is hidden.
    const calculationRevision = {
      documentUID: calculationDocumentUID,
      ...Object.fromEntries(
        Object.entries(formSnapshot).filter(
          ([key]) => !NON_CALCULATION_FIELD_KEYS.has(key),
        ),
      ),
    };

    // Everything for this revision - design, documentUID, drawingScale,
    // shell, nozzle, estimation and calculation - lives together in one
    // place: inputData[currentRevision] inside the "all" record. There is
    // no separate shellDesign/nozzlesDesign record anymore.
    const inputDataRevision = {
      documentUID,
      design: { dataVessel, dataTank, dataEquip },
      shell: {
        shellDesign: shellRows.map((row, i) => ({ ...row, rowNo: i })),
      },
      nozzle: {
        DataGridView1: nozzleRowsRaw,
        DataGridView10: geometryRowsRaw,
        nozzlesDesign: nozzleRows,
      },
      drawingScale: formSnapshot?.drawingScale,
      estimation: estimationRevision,
      calculation: calculationRevision,
    };

    const existingAllRes = await getAllData(activeProjectId).catch(() => null);
    const existingAllData = existingAllRes?.all || {};

    const existingInputRevisions = Object.fromEntries(
      Object.entries(existingAllData?.inputData || {}).filter(
        ([k]) => !isNaN(Number(k)),
      ),
    );

    existingInputRevisions[currentRevision] = cloneData(inputDataRevision);

    // Drop any leftover top-level shellDesign/nozzlesDesign/estimation/
    // calculation keys from older saves - everything now lives only inside
    // inputData[rev].
    const {
      shellDesign: _droppedShellDesign,
      nozzlesDesign: _droppedNozzlesDesign,
      estimation: _droppedEstimation,
      calculation: _droppedCalculation,
      ...restExistingAllData
    } = existingAllData;

    try {
      await postAllData(activeProjectId, {
        ...restExistingAllData,
        inputData: existingInputRevisions,
      });
    } catch (error) {
      throw new Error("Failed to save PV input data");
    }

    return {
      ok: true,
      failedCount: 0,
    };
  }

  async function loadPvData(projectIdValue) {
    if (!projectIdValue) return;

    const allDataRes = await getAllData(projectIdValue).catch(() => null);
    const allData = allDataRes?.all || {};

    let inputDataRevision = allData?.inputData?.[currentRevision];
    if (!inputDataRevision || Object.keys(inputDataRevision).length === 0) {
      const allRevKeys = Object.keys(allData?.inputData || {})
        .filter((k) => !isNaN(Number(k)))
        .sort((a, b) => Number(b) - Number(a));
      const latestRev = allRevKeys.find((r) => r !== currentRevision);
      inputDataRevision = latestRev ? allData.inputData[latestRev] : {};
    }

    const restoredDesignPayload = {
      dataVessel: inputDataRevision?.design?.dataVessel,
      dataTank: inputDataRevision?.design?.dataTank,
      dataEquip: inputDataRevision?.design?.dataEquip,
    };

    // Shell/nozzle design rows now live only inside this revision - there
    // is no separate shellDesign/nozzlesDesign record to fall back to.
    const nextDgv5 = normalizeShellRows(inputDataRevision?.shell);
    const nextNozzleData = normalizeNozzleRows(inputDataRevision?.nozzle);

    const nextFormValues = cloneData(getValues());
    assignDesignFields(nextFormValues, restoredDesignPayload);

    const projectTag = nextFormValues.tagNo || "";
    nextDgv5.forEach((row) => {
      if (!row.UID && row.Component) {
        row.UID = computeShellUID(row.Component, projectTag) || "";
      }
    });

    nextNozzleData.DataGridView1.forEach((row) => {
      if (!row.UID && row.Mark) {
        row.UID = computeNozzleUID(row.Mark, projectTag) || "";
      }
    });

    nextFormValues.DataGridView5 = nextDgv5;
    nextFormValues.DataGridView1 = nextNozzleData.DataGridView1;
    nextFormValues.DataGridView10 = nextNozzleData.DataGridView10;

    // Restore estimation tab data - kept alive even though its tab is
    // currently hidden, so it survives round trips through save/load.
    const estData = inputDataRevision?.estimation || {};
    nextFormValues.DataGridView2 = estData.DataGridView2 || [];
    nextFormValues.DataGridView7 = estData.DataGridView7 || [];
    nextFormValues.DataGridView8 = estData.DataGridView8 || [];
    nextFormValues.bomData = estData.bomData || [];
    nextFormValues.totalMaterialCost = estData.totalMaterialCost;
    nextFormValues.totalHours = estData.totalHours;
    nextFormValues.totalFabCost = estData.totalFabCost;
    nextFormValues.totalCost = estData.totalCost;
    nextFormValues.TextBox41 = estData.TextBox41;
    nextFormValues.TextBox35 = estData.TextBox35;

    // Restore calculation tab data - same reasoning as estimation above.
    const calcData = inputDataRevision?.calculation || {};
    for (const [key, value] of Object.entries(calcData)) {
      nextFormValues[key] = value;
    }

    nextFormValues.drawingScale = inputDataRevision?.drawingScale || "";

    reset(nextFormValues);
    setRevisionLoadVersion((version) => version + 1);
  }

  useEffect(() => {
    if (!projectId) return;

    setSelectedProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (!selectedProjectId) return;

    if (
      !confirmedRevision ||
      confirmedRevision.projectId !== selectedProjectId ||
      confirmedRevision.revision !== currentRevision
    ) {
      return;
    }

    loadPvData(selectedProjectId);
  }, [
    confirmedRevision,
    currentRevision,
    getValues,
    reset,
    selectedModelingRevision,
    selectedProjectId,
    setValue,
  ]);

  return (
    <PVContext.Provider
      value={{
        projectId: activeProjectId,
        selectedProjectId,
        setSelectedProjectId,
        control,
        setValue,
        getValues,
        currentRevision,
        revisionLoadVersion,
        savePVData,
        loadPvData,
        confirmedRevision,
        setConfirmedRevision,
      }}
    >
      {children}
    </PVContext.Provider>
  );
}

export { PVProvider, PVContext };
